(ns livefeed.feed
  (:require [cljs.core.async :refer-macros [go go-loop] :refer [<! timeout]]
            [reagent.core :as reagent]
            [reagent.ratom :refer-macros [reaction]]
            [livefeed.api :as api]
            [livefeed.message-status :as status]
            [livefeed.moderation :as moderation]
            [livefeed.properties :as properties]
            [livefeed.state :as state]
            [livefeed.util :refer [queue pop-n throttle]]))

(defonce message-likes (reagent/atom {}))
(defonce livefeed-messages (reagent/atom (queue)))
(defonce best-messages (reagent/atom (queue)))
(defonce unseen-live-messages (reagent/atom 0))
(defonce quoted-message (reagent/atom nil))
(defonce reported-message (reagent/atom nil))
(def labeled-messages (reagent/atom []))

(add-watch quoted-message :quote-message
           (fn [_ _ old-val new-val]
             (let [event (js/CustomEvent. "quote-message"
                                          #js {:detail (clj->js {:old old-val
                                                                 :new new-val})})]
               (.dispatchEvent js/document event))))

;; Queue for accepted-comment messages that arrive before user state is restored
(defonce pending-accepted-messages (reagent/atom []))

(def messages-with-likes (reaction (doall (for [msg @livefeed-messages]
                                            (merge msg (get @message-likes (:comment-id msg)))))))

(defonce at-feed-start? (reagent/atom false))

(defn reset-feed! []
  (reset! livefeed-messages (queue))
  (reset! best-messages (queue))
  (reset! unseen-live-messages 0)
  (reset! reported-message (reagent/atom nil))
  (reset! quoted-message nil)
  (reset! pending-accepted-messages []))

(defn accepted-comment? [message]
  (= "accepted-comment" (:type message)))

(defn starred? [message]
  (some #{"starred"} (:labels message)))

(defn best? [message]
  (or (= "writer" (:role message))
      (starred? message)))

(defn handle-own-message-received [message]
  (if (and @state/user (= (:id @state/user) (get-in message [:chat-user :id])))
    (do
      (moderation/comment-moderated! message)
      (status/notify! message))
    ;; If user state is not available yet, queue the message for later processing
    (when (accepted-comment? message)
      (swap! pending-accepted-messages conj message))))

(defn process-pending-accepted-messages! []
  (when (and (seq @pending-accepted-messages) @state/user)
    (doseq [message @pending-accepted-messages]
      (handle-own-message-received message))
    (reset! pending-accepted-messages [])))

(defn ->message-like [message]
  [(:comment-id message) {:likes          (:likes message)
                          :user-has-liked (:user-has-liked message)}])

(defn ->message-likes [messages]
  (map ->message-like messages))

(defn init-messages [messages best?]
  (reset! (if best? best-messages livefeed-messages) (queue messages))
  (reset! message-likes (into {} (->message-likes messages))))

(defn add-labeled-message [message]
  (when (best? message)
    (swap! message-likes conj (->message-like message))
    (swap! best-messages conj message)))

(defn remove-old-messages [messages]
  (when (and @state/is-at-bottom
             (< 100 (count @messages)))
    (reset! at-feed-start? false)
    ;; remove all but 90 last items from messages
    (swap! messages pop-n (- (count @messages) 90))
    true))

(defn add-new-message [message]
  (when (accepted-comment? message)
    (swap! livefeed-messages conj message)
    (swap! message-likes conj (->message-like message))
    (swap! unseen-live-messages inc)
    (when (best? message)
      (swap! best-messages conj message))
    (let [removed-livefeed? (remove-old-messages livefeed-messages)
          removed-best? (remove-old-messages best-messages)]
      (when (or removed-livefeed? removed-best?)
        (reset! message-likes (into {} (concat (->message-likes @livefeed-messages)
                                               (->message-likes @best-messages))))))))

(defn add-new-messages [new-messages]
  (reset! state/is-at-bottom false)
  (run! handle-own-message-received new-messages)
  (swap! livefeed-messages into new-messages)
  (swap! best-messages into (filter best? new-messages))
  (swap! unseen-live-messages + (count new-messages))
  (swap! message-likes into (->message-likes new-messages)))

(defn remove-message-by-id [message-id]
  (let [remove-message (partial filter #(not= message-id (:comment-id %)))]
    (swap! livefeed-messages (comp queue remove-message))
    (swap! best-messages (comp queue remove-message))))

(defn add-previous-messages [previous-messages best?]
  (swap! (if best? best-messages livefeed-messages)
         #(queue (distinct (concat previous-messages %))))
  (swap! message-likes into (->message-likes previous-messages)))

(defn update-message-likes [message-id new-amount-of-likes]
  (swap! message-likes (fn [m]
                         (if (contains? m message-id)
                           (assoc-in m [message-id :likes] new-amount-of-likes)
                           m))))

(defn toggle-user-has-liked-message [message-id]
  (swap! message-likes (fn [m]
                         (let [liked? (not (get-in m [message-id :user-has-liked]))]
                           (-> m
                               (update-in [message-id :user-has-liked] not)
                               (update-in [message-id :likes] (if liked? inc dec)))))))

(defn is-user-liked-message [message-id]
  (get-in @message-likes [message-id :user-has-liked] false))

(defn load-next-messages []
  (when-let [newest-timestamp (:acception-time (last @livefeed-messages))]
    (go (let [chat-id (properties/get-property :chatId)
              messages (<! (api/get-messages-after chat-id newest-timestamp false))]
          (when (seq messages)
            (add-new-messages messages))))))

(defn- load-previous-messages* [best?]
  (go (when-let [oldest-timestamp (:acception-time (peek (if best? @best-messages @livefeed-messages)))]
        (let [chat-id (properties/get-property :chatId)
              messages (<! (api/get-messages-before chat-id oldest-timestamp best?))]
          (if (seq messages)
            (add-previous-messages messages best?)
            (reset! at-feed-start? true))))))

(def load-previous-messages (throttle load-previous-messages* 500))

(defn load-initial-messages [chat-id best?]
  (go (let [messages (<! (api/get-initial-messages chat-id best?))]
        (init-messages messages best?))))

(defn poll-labeled-messages! [label]
  (go-loop []
    (reset! labeled-messages (<! (api/get-labeled-messages (properties/get-property :chatId) label)))
    (<! (timeout 10000))
    (recur)))
